import EditDeviceTemplate from './editDevice.html';
import EditDeviceDataProviderModule from './editDeviceDataProvider.js';
import ComponentsModule from './components/components.js';
import TabsModule from './tabs/tabs.js';

const SELECTED_DEVICE_LOCAL_STORAGE_KEY = 'Selected-Device';

export default angular
  .module('main.devices.edit', [
    EditDeviceDataProviderModule,
    ComponentsModule,
    TabsModule
  ])
  .config(EditDeviceRouter)
  .name;

function EditDeviceRouter($urlRouterProvider, $stateProvider) {
  $stateProvider
    .state('main.devices.edit', {
      url: `/edit/:deviceId?:userId&:tab&:from&:locationId`,
      template: EditDeviceTemplate,
      controller: EditDeviceController,
      params: {
        device: null,
        deviceId: {
          squash: true,
          value: null
        }
      }
    });
}

function EditDeviceController($scope,
                              $rootScope,
                              $q,
                              $filter,
                              $state,
                              EditDeviceDataProvider,
                              utils,
                              config,
                              ToastsService) {

  const $t = $filter('translate');
  const $deviceIcon = $filter('deviceIcon');

  // Clear local storage device if in URL we have another ID
  if ($state.params.deviceId && $state.params.deviceId != utils.getStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY)?.id) {
    utils.removeStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY);
  }

  // NOTE dirty hack for fast injecting ready-to-use data provider to child components
  EditDeviceDataProvider.instance = new EditDeviceDataProvider();
  let dataProvider = EditDeviceDataProvider.instance;
  $scope.dataProvider = dataProvider;

  // #region Navigation Tabs
  $scope.tabs = [
    {
      id: 'summary',
      name: $t('devices.edit.tabs.summary'),
      icon: 'far fa-file-alt'
    },
    {
      id: 'edit',
      name: $t('devices.edit.tabs.edit'),
      icon: 'far fa-file-alt',
      hidden: true,
      silent: true
    },
    {
      id: 'location',
      name: $t('devices.edit.tabs.location'),
      icon: 'far fa-map'
    },
    {
      id: 'devices',
      name: $t('devices.edit.tabs.devices'),
      icon: 'iotr fa-gateway',
      hidden: true // will be changed below
    },
    {
      id: 'subregions',
      name: $t('regions.title'),
      icon: 'far fa-cube',
      hidden: true // will be changed below
    },
    {
      id: 'radar',
      name: $t('regions.radarSetup'),
      icon: 'far fa-cube',
      hidden: true
    },
    {
      id: 'boundaries',
      name: $t('regions.boundaries.title'),
      icon: 'far fa-cube',
      hidden: true
    },
    {
      id: 'parameters',
      name: $t('devices.edit.tabs.parameters'),
      icon: 'far fa-heart-rate',
    },
    {
      id: 'firmware',
      name: $t('devices.edit.tabs.firmware'),
      icon: 'far fa-microchip',
      hidden: true
    },
    {
      id: 'rules',
      name: $t('devices.edit.tabs.rules'),
      icon: 'iotr fa-rules'
    },
    {
      id: 'goals',
      name: $t('devices.edit.tabs.goals'),
      icon: 'far fa-brain'
    },
    {
      id: 'stories',
      name: $t('devices.edit.tabs.stories'),
      icon: 'far fa-box-open'
    },
    {
      id: 'files',
      name: $t('devices.edit.tabs.files'),
      icon: 'far fa-folders'
    },
    {
      id: 'measurements',
      name: $t('devices.edit.tabs.measurements'),
      icon: 'far fa-book',
      hidden: true
    },
    {
      id: 'cellular',
      name: $t('devices.edit.tabs.cellular'),
      icon: 'far fa-sim-card',
      hidden: true
    },
  ];

  // #endregion

  angular.extend($scope, {
    onEditDeviceButtonClick: onEditDeviceButtonClick,
    canDeleteDevice: canDeleteDevice,
    onDeleteDeviceButtonClick: onDeleteDeviceButtonClick,
    copyToClipboard: $rootScope.copyToClipboard,
  });

  init();

  function init() {
    $scope.loading = true;

    $q.all([
      checkPermissions(),
      dataProvider.userIdResource.init(),
      dataProvider.deviceResource.init(),
      dataProvider.deviceLocationResource.init(),
    ])
      .then(() => {
        // Set default tab, only if main resources loaded successfully
        dataProvider.activeTab = $state.params.tab || 'summary';
      }, (err) => {
        if (err?.resultCode === 7) { // Access denied
          $scope.accessDeniedError = true;
          $scope.isError = true;
          $scope.errorTitle = $t('devices.edit.errors.accessDenied.title');
          $scope.errorDesc = $t('devices.edit.errors.accessDenied.desc');
          return $q.reject();
        } else if (err?.resultCode === 8) { // Object not found
          $scope.notFoundError = true;
          $scope.isError = true;
          $scope.errorTitle = $t('devices.edit.errors.notFound.title');
          $scope.errorDesc = $t('devices.edit.errors.notFound.desc');
          return $q.reject();
        }
        return $q.reject(err);
      })
      .then(() => $q.all([
        dataProvider.deviceTypeResource.init(),
        dataProvider.deviceModelCategoryResource.init(),
        dataProvider.deviceModelResource.init(),
        dataProvider.isGatewayResource.init(),
        dataProvider.isZoneResource.init(),
        dataProvider.canIDeleteDeviceResource.init(),
        dataProvider.isRadarResource.init(),
      ]))
      .then(() => {
        $scope.loading = false;
      }, (err) => {
        $scope.loading = false;
        if (err) {
          utils.handleApiError(err);
        }
        dataProvider.goBack();
      });

    // Enable devices tab for gateways and virtual zone devices
    $scope.$watchGroup([
      'dataProvider.isGatewayResource.value',
      'dataProvider.isZoneResource.value'
    ], () => {
      $scope.tabs.find(t => t.id === 'devices').hidden =
        !(dataProvider.isGatewayResource.value || dataProvider.isZoneResource.value);
    });

    // Enable regions tab only for Radar devices
    $scope.$watch('dataProvider.isRadarResource.value', () => {
      $scope.tabs.find(t => t.id === 'subregions').hidden = (dataProvider.isRadarResource.value === false);
    });

    // #region Device icon recalculation
    $scope.$watchGroup([
      'dataProvider.deviceTypeResource.value',
      'dataProvider.deviceResource.value',
      'dataProvider.deviceModelCategoryResource.value',
      'dataProvider.deviceModelResource.value',
    ], () => {
      let device = dataProvider?.deviceResource?.value;
      let deviceType = dataProvider?.deviceTypeResource?.value;
      let deviceModelCategory = dataProvider?.deviceModelCategoryResource?.value;
      let deviceModel = dataProvider?.deviceModelResource?.value;

      let deviceTypeAttrIcon = device?.typeAttributes?.find(ta => ta.name === 'deviceListIcon')?.value;
      let deviceTypeIcon = deviceType ? utils.getDeviceTypeIconName(deviceType) : undefined;
      let deviceModelIcon = deviceModel?.icon; // NOTE: not sure if such field ever exists
      let deviceModelCategoryIcon = deviceModelCategory?.icon;

      let icon = device?.icon || deviceTypeAttrIcon || deviceTypeIcon || deviceModelIcon || deviceModelCategoryIcon;
      $scope.deviceIcon = icon ? $deviceIcon(icon, 'r') : 'far fa-satellite-dish';
    });
    // #endregion

    $scope.$on('$destroy', () => {
      dataProvider.destroy();
      EditDeviceDataProvider.instance = undefined;
      $('#delete-device-dialog').remove();
    });
  }

  /**
   * Check if we have enough permissions to see this device
   * @return {Promise}
   */
  function checkPermissions() {
    return dataProvider.canIEditDeviceResource.init()
      .then((allowed) => {
        if (allowed === false) {
          ToastsService.error($t('devices.edit.wrongPermissions.title'), $t('devices.edit.wrongPermissions.desc'));
          dataProvider.goBack();
        }
      });
  }

  function onEditDeviceButtonClick() {
    if (dataProvider.activeTab === 'edit') {
      $scope.dataProvider.activeTab = $scope.dataProvider.previousTab || 'summary';
      return;
    }
    dataProvider.activeTab = 'edit';
  }

  function canDeleteDevice() {
    return dataProvider?.canIDeleteDeviceResource?.value;
  }

  function onDeleteDeviceButtonClick() {
    $('#delete-device-dialog')
    .modal({
      autofocus: false,
      closable: true,
      detachable: true,
      onApprove: function() {
        dataProvider.deleteDevice();
      }
    })
    .modal('show');
  }

}
